import { event, utils } from '../../Foundation';

export class Select {
  /**
   * Creates a new instance of the Video class for handling HTML5 Video, Youtube Videos and 23Videos
   * @param {HTMLElement} element Video DOM element
   */
  constructor(element) {
    // Internals
    element.Select = this;
    this.base = element;

    // Base initialization
    this.el = element.querySelector('select');

    // DOM check
    if (this.el == null) return;

    // Events
    this._addEvent('reflow', this._onItemReflow);
    this._addEvent('refresh', this._onItemUpdate);
    this._addEvent('updateLabel', this._updateLabelCount);

    // Build
    if (!this._checkPostback) {
      this._build();
    }
  }

  get _checkPostback() {
    return this.base.querySelector('.form-select__el');
  }

  /**
   * Watcher for closing dropdown in case current element looses focus
   * @param {HTMLElement} - Current Event Target
   */
  _onKeyUp({ target }) {
    if (!this.base.contains(target)) {
      this.wrapper.classList.remove('is-active');

      if (this.el.multiple) this._onMultipleItemChange();

      return;
    }
  }

  /**
   * Watcher for handling keyboard feedback from user
   * @param {Event} e - Event Interface
   */
  _onKeyDown(e = Event) {
    if (!this.base.contains(e.target)) {
      return;
    }

    const keyCode = e.keyCode,
      arrOptions = Array.from(this.options.children);

    // Feedback for using Enter
    if (keyCode === 13) {
      e.preventDefault();
      e.stopPropagation();

      if (this.options.contains(e.target)) {
        if (this.el.multiple) {
          e.target.querySelector("input[type='checkbox']").click();
          return;
        }

        this.onSingleItemClick(e);
        this.wrapper.focus();
        return;
      }

      this.placeholder.click();
    }

    // Feedback for using Arrow Up / Arrow Down
    if (keyCode === 38 || keyCode === 40) {
      e.preventDefault();
      e.stopPropagation();

      if (!this.wrapper.classList.contains('is-active')) {
        this.placeholder.click();
      }

      utils
        .getNextActiveElement(
          arrOptions,
          e.target.parentNode,
          e.keyCode === 40,
          !arrOptions.includes(e.target),
        )
        .children[0].focus();
    }

    // Feedback for using ESC
    if (keyCode === 27) {
      if (this.wrapper.classList.contains('is-active')) {
        e.preventDefault();
        e.stopPropagation();

        this.placeholder.click();
        this.wrapper.focus();
      }
    }

    // Feedback for using SPACE
    if (keyCode === 32) {
      e.preventDefault();
      e.stopPropagation();

      if (this.options.contains(e.target)) {
        if (this.el.multiple) {
          const oItem = e.target,
            oItemCheckbox = oItem.querySelector('input[type="checkbox"]');

          oItemCheckbox.checked = !oItemCheckbox.checked;

          return;
        }

        this.onSingleItemClick(e);
        this.wrapper.focus();
        return;
      }

      this.placeholder.click();
    }
  }

  /**
   * Build
   * Construct markup, events eg. for showing dropdownlist
   */
  _build() {
    const applyA11yContent = (elem) => {
      elem.setAttribute('role', 'button');
      elem.setAttribute('aria-haspopup', 'listbox');
      elem.setAttribute('aria-expanded', 'false');
      // Add tabindex to dropbox div
      elem.setAttribute('tabindex', '0');
    };

    // Wrapper
    this.wrapper = document.createElement('div');
    this.wrapper.className = 'form-select__el' + (this.state ? ' is-disabled' : '');

    this.onWrapperClick = this._onWrapperClick.bind(this);

    // Options
    this.options = document.createElement('ul');
    this.options.className = 'form-select__list';
    this.options.setAttribute('role', 'listbox');

    // Searchable
    if (this._data.searchable) {
      this.searchable = document.createElement('li');
      this.searchable.className = 'form-select__list-item is-searchable';

      this.query = document.createElement('input');
      this.query.setAttribute('type', 'search');
      this.query.className = 'form-control';

      this.query.addEventListener('keydown', this._onSearchableKeyDown);
      this.query.addEventListener('keyup', this._onSearchableKeyUp.bind(this));

      this.searchable.appendChild(this.query);
      this.options.appendChild(this.searchable);
    }

    for (let i = 0; this.items.length > i; i++) {
      if (typeof this.items[i] !== 'undefined' || this.items[i] !== null) {
        let item = this.items[i],
          onSingleOptionClick = this.el.multiple ? null : this.onSingleItemClick.bind(this),
          option = this._createSelectableItem(item, onSingleOptionClick);

        this.options.appendChild(option);
      }
    }

    // Placeholder
    this.placeholder = document.createElement('span');
    this.placeholder.className = 'form-select__placeholder';
    this.placeholder.innerHTML = this._data.placeholder;
    if (this.items.length && this.selectedItems.length > 0) {
      let selectedItems =
        this.selectedItems[0].innerHTML ||
        this.options.querySelector('.form-select__list-item')?.innerHTML;
      if (this.selectedOption) {
        selectedItems = this.selectedOption.closest('.form-select__list-item')?.innerHTML;
      }
      this.placeholder.innerHTML = this.el.multiple ? this._data.placeholder : selectedItems;
    }

    // State management
    if (!this.state) {
      this.placeholder.addEventListener('click', this.onWrapperClick);
      this.placeholder.addEventListener('keyup', (e) => {
        if (e.key === 'ENTER') {
          this.wrapper.click();
        }
      });
      applyA11yContent(this.placeholder);
    }

    this.wrapper.append(this.options, this.placeholder);

    // Append wrapper to selector
    this.el.parentNode.insertBefore(this.wrapper, this.el.nextSibling);
    document.addEventListener('keydown', this._onKeyDown.bind(this));
    document.addEventListener('keyup', this._onKeyUp.bind(this));

    if (this.base.getAttribute('data-mode') === 'inc-count') {
      this._updatePlaceholderCount();
    }

    // Set postion of dropdown list
    this._calcDropdownPosition();
  }

  get selectedOption() {
    // Check if any option is selected
    const selected = this.selectedItems
      .filter((el) => el.selected && el.value)
      .map((el) => el.innerText);
    if (selected.length <= 0) return false;
    // Get dropdown item that matches option
    const selectOption = [...this.options.querySelectorAll('.form-select__list-label')].filter(
      (el) => el.innerText === selected[0],
    );
    return selectOption[0];
  }

  /**
   * _createSelectableItem
   * Create an item (option) based on type of dropdown
   * @param {Object} item - item properties
   * @param {Function} callback - Function to execute on click
   * @returns {HTMLElement}
   */
  _createSelectableItem(item, callback) {
    if (item === null) return false;

    let uniqueId = (Math.random() + 1).toString(36).substring(7),
      oControl = document.createElement('li'),
      oInner = document.createElement('div');

    oControl.setAttribute(
      'class',
      'form-select__list-item' + (item.disabled ? ' is-disabled' : ''),
    );
    oControl.setAttribute('role', 'option');

    if (this.el.multiple) {
      // Multiple
      // Checkbox
      const checkboxID = `${item.getAttribute('name')}-${uniqueId}`;
      let cb = document.createElement('input');
      cb.setAttribute('type', 'checkbox');
      cb.setAttribute('name', checkboxID);
      cb.setAttribute('id', checkboxID);
      cb.setAttribute('value', item.value);

      if (item.getAttribute('selected')) {
        cb.setAttribute('checked', 'checked');
      } else {
        item.selected = false;
      }

      // Label
      let label = document.createElement('label');
      label.setAttribute('for', checkboxID);
      label.innerHTML = item.innerText || item.innerHTML;

      oInner.appendChild(cb);
      oInner.appendChild(label);
    } else {
      // Single
      // Image
      if (item.dataset.imageUrl) {
        const image = document.createElement('img');
        image.src = item.dataset.imageUrl;
        oInner.appendChild(image);
      }

      // Label
      const label = document.createElement('span');
      label.className = 'form-select__list-label';
      label.innerHTML = item.innerText || item.innerHTML;

      // Add count
      if (item.getAttribute('data-count')) {
        label.setAttribute('data-count', item.getAttribute('data-count'));
      }

      oInner.appendChild(label);

      if (item.dataset.sublabel) {
        // Label
        const subLabel = document.createElement('span');
        subLabel.className = 'form-select__list-subLabel';
        subLabel.innerHTML = item.dataset.sublabel ?? '';
        oInner.appendChild(subLabel);
      }
      // Click
      if (typeof callback === 'function') {
        oInner.addEventListener('click', callback);
      }
    }

    oControl.appendChild(oInner);

    return oControl;
  }

  /**
   * addEvent
   * Attach customEvent listener
   * @param {String} ev - event name
   * @parasm {Function} cb - callback
   */
  _addEvent(ev, cb) {
    let event;

    if (typeof Event === 'function') {
      event = new CustomEvent(ev);
    } else {
      event = document.createEvent('CustomEvent');
      event.initCustomEvent(ev, false, false, {});
    }

    this.el.addEventListener(ev, cb.bind(this));
  }

  /**
   * onItemUpdate
   * Programmaticly trigger an update from global scope
   * @param {Event} e - Event interface
   */
  _onItemUpdate() {
    let items = [];

    this.selectedItems.forEach((x) => items.push(x.innerText || x.innerHTML));
    this.placeholder.innerHTML = items.join(',');
  }

  /**
   * onMultipleItemChange
   * Handler for updating select to reflect selected values
   * @param {Event} e - Event interface
   */
  _onMultipleItemChange() {
    let boolChanged = false;

    // Update select box to reflect change
    let items = Array.from(this.options.children);

    items.forEach((x) => {
      let cb = x.querySelector('input[type="checkbox"]'),
        index = items.indexOf(x);

      if (
        cb != null &&
        ((cb.checked && !this.el.options[index].selected) ||
          (!cb.checked && this.el.options[index].selected))
      ) {
        boolChanged = true;
        this.el.options[index].selected = cb.checked;
      }
    });

    // Trigger update in case a change has occoured
    if (boolChanged) {
      event.emit(this.el, 'change');
    }
  }

  /**
   * onItemReflow
   * Reflow entire component to align state-changes
   * Used a timeout to allow IE11 to reflect changes done from configurator
   */
  _onItemReflow() {
    if (this.state) {
      this.wrapper.classList.add('is-disabled');
      this.placeholder.setAttribute('tabindex', '-1');

      // Remove eventlistener
      this.placeholder.removeEventListener('click', this.onWrapperClick);
    } else {
      this.wrapper.classList.remove('is-disabled');
      this.placeholder.setAttribute('tabindex', '0');

      // Add event listener
      this.placeholder.addEventListener('click', this.onWrapperClick);
    }

    // Update state for items
    for (let i = 0; this.items.length > i; i++) {
      if (typeof this.items[i] !== 'undefined' || this.items[i] !== null) {
        const option = this.items[i],
          element = this.options.children[i];

        if (option.disabled) {
          element.classList.add('is-disabled');
        } else {
          element.classList.remove('is-disabled');
        }
      }
    }
  }

  /**
   * items
   * Get all children/items of dropdown
   * @returns NodeList
   */
  get items() {
    return this.el.options;
  }

  /**
   * selectedItems
   * Get all pre-selected items from element
   * @returns Array
   */
  get selectedItems() {
    return Array.from(this.el.options).filter((item) => item.selected);
  }

  /**
   * filteredItems
   * Get all items where item is not disabled and index are higher than zero
   * @returns Array
   */
  get filteredItems() {
    return Array.from(this.el.options).filter((item, index) => {
      return !item.disabled && index > 0;
    });
  }

  /**
   * State
   * Check current state of element
   * @returns {Boolean}
   */
  get state() {
    let x = false;
    if (
      this.el.hasAttribute('disabled') ||
      this.el.disabled ||
      this.el.classList.contains('is-disabled')
    ) {
      x = true;
    } else if (!this.el.options.length) {
      x = true;
    }

    return x;
  }

  /**
   * Data
   * Build a set of variables for current instance
   * @returns {Object}
   */
  get _data() {
    let x = this.base.dataset.select;
    x = x.length ? JSON.parse(x) : {};

    return { ...this.settings, ...x };
  }

  _updateLinkedId() {
    const selectElement = this.base.querySelector('select');
    if (!selectElement.dataset.id) return;
    const linkedElement = document.getElementById(selectElement.dataset.id);
    if (linkedElement != null && selectElement != null) linkedElement.value = selectElement.value;
  }
  /**
   * onWrapperClick
   * Handler for interacting with placeholder
   * @param {Object} e - Event
   */
  _onWrapperClick() {
    let placeholderWrapper = this.base.querySelector('.form-select__el'),
      scope = (e) => {
        let triggered = this.base.contains(e.target);
        if (!triggered) {
          placeholderWrapper.classList.remove('is-active');
          document.removeEventListener('click', scope);

          if (this.el.multiple) {
            this._onMultipleItemChange();
          }
        }
        this._updateLinkedId();
      },
      scroll = () => {
        placeholderWrapper.classList.remove('is-active');
        if (this.el.multiple) {
          this._onMultipleItemChange();
        }
        document.removeEventListener('scroll', scroll);
        this._updateLinkedId();
      };

    if (placeholderWrapper.classList.contains('is-active')) {
      placeholderWrapper.classList.remove('is-active');
      placeholderWrapper.setAttribute('aria-expanded', 'false');

      Array.from(this.options.children).forEach((x) => {
        Array.from(x.children).forEach((y) => y.setAttribute('tabindex', '-1'));
      });

      // Update multiple selection when click on placeholder
      if (this.el.multiple) {
        this._onMultipleItemChange();
      }
      document.removeEventListener('click', scope);
    } else {
      placeholderWrapper.classList.add('is-active');
      placeholderWrapper.setAttribute('aria-expanded', 'true');

      Array.from(this.options.children).forEach((x) => {
        Array.from(x.children).forEach((y) => {
          // When dropbox is open only make items that are not disabled tabable
          if (!y.parentElement.className.includes('is-disabled')) {
            y.setAttribute('tabindex', '0');
          }
        });
      });

      document.addEventListener('click', scope);
      document.addEventListener('scroll', scroll);
    }
  }

  /**
   * onItemClick
   * Handler for interacting with items within dropdown
   * @param {Object} e - Event
   */
  onSingleItemClick(e) {
    let target = e.target;

    if (e.target.parentElement.classList.contains('is-disabled')) {
      e.preventDefault();
      return false;
    }

    // When an item is selected disable tab on all dropdown items
    Array.from(this.options.children).forEach((x) => {
      Array.from(x.children).forEach((y) => y.setAttribute('tabindex', '-1'));
    });

    // Close wrapper
    this.wrapper.classList.remove('is-active');

    // Update placeholder
    this.placeholder.innerHTML =
      target.firstChild.innerText ||
      target.value ||
      target.closest('.form-select__list-item').innerHTML;

    // Update select box to reflect change
    const children = Array.from(this.options.children).filter(
        (x) => !x.classList.contains('is-searchable'),
      ),
      index = children.indexOf(e.target.closest('li'));

    if (typeof this.el.options[index] !== 'undefined' && !this.el.options[index].selected) {
      this.el.options[index].selected = true;
      // Trigger event flow
      event.emit(this.el, 'change');
    }

    // Add count
    if (this.base.getAttribute('data-mode') === 'inc-count') {
      this._updateLabelCount();
    }
  }

  _updateLabelCount() {
    const optionItems = this.el
      .closest('.datatable')
      .querySelectorAll('.datatable__filters [data-select] option');
    const labelItems = this.el
      .closest('.datatable')
      .querySelectorAll('.datatable__filters [data-select] .form-select__list-item');
    for (const [i, item] of optionItems.entries()) {
      const formLabel = labelItems[i];
      const newValue = item.getAttribute('data-count');
      formLabel.querySelector('span').setAttribute('data-count', newValue);
    }
    this._updatePlaceholderCount();
  }

  _updatePlaceholderCount() {
    const placeholders = this.el
      .closest('.datatable')
      .querySelectorAll('.datatable__filters [data-select] .form-select__placeholder');
    const firstOptionItem = this.el
      .closest('.datatable')
      .querySelectorAll('.datatable__filters [data-select] option')[0];
    for (const ph of placeholders) {
      ph.setAttribute('data-count', firstOptionItem.getAttribute('data-count'));
    }
  }

  /**
   * onSearchableKeyDown
   * Prevent a user from using different keys
   * @param {Event} e - Event interface
   */
  _onSearchableKeyDown(e) {
    if (e.keyCode === 13) {
      e.preventDefault();
      return false;
    }
  }

  /**
   * onSearchableKeyUp
   * Execute a search in available items and toggl class to show/hide related items
   * @param {Event} e - Event interface
   */
  _onSearchableKeyUp(e) {
    const items = Array.from(this.options.children);
    items.forEach((x) => {
      const value = x.querySelector('span');
      if (value != null) {
        if (value.innerText.toLowerCase().indexOf(e.target.value.toLowerCase().trim()) !== -1) {
          x.classList.remove('is-hidden');
        } else {
          x.classList.add('is-hidden');
        }
      }
    });
  }

  _calcDropdownPosition() {
    if (this._dropdownPositionTop) {
      this.base.querySelector('.form-select__list').classList.add('position_top');
    }
  }

  get _dropdownPositionTop() {
    return (
      document.body.getBoundingClientRect().bottom - this.base.getBoundingClientRect().bottom < 300
    );
  }

  /**
   * Settings
   * Default settings for feature
   * @returns {Object}
   */
  get settings() {
    return {
      placeholder: 'Select',
      style: {
        isActive: 'is-active',
      },
      multiple: {
        button: 'Apply',
      },
      searchable: false,
    };
  }
}
